import { Avatar } from "@mui/material";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { BsArrowLeft, BsEye, BsEyeSlash } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Yup from "yup";
import { postLogin } from "../redux/features/LoginSlice";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.login.isLoading);
  const me = useSelector((state) => state.login.me);

  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (me?.isActive) {
      navigate("/");
    }
  }, [me, navigate]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex items-center justify-center min-h-screen bg-gradient-to-r from-blue-500 to-purple-600"
    >
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="w-full max-w-md p-8 bg-white shadow-2xl bg-opacity-20 backdrop-blur-md rounded-xl"
      >
        <div className="flex flex-col items-center gap-4">
          <Avatar sx={{ width: 80, height: 80, bgcolor: "purple" }} />
          <h2 className="text-2xl font-bold text-white">
            Log in to your account
          </h2>
        </div>

        <Formik
          initialValues={{
            email: "",
            password: "",
          }}
          validationSchema={Yup.object({
            email: Yup.string()
              .email("Invalid email address")
              .required("Required"),
            password: Yup.string().required("Required"),
          })}
          onSubmit={(values) => {
            dispatch(postLogin({ data: values, navigate }));
          }}
        >
          <Form className="mt-6 space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-white"
              >
                Email
              </label>
              <Field
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                className="w-full px-4 py-3 mt-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <ErrorMessage
                name="email"
                component="div"
                className="mt-1 text-sm text-red-200"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-white"
              >
                Password
              </label>
              <div className="relative">
                <Field
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 mt-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  className="absolute text-gray-500 transform -translate-y-1/2 top-1/2 right-4 hover:text-purple-600"
                >
                  {showPassword ? (
                    <BsEyeSlash size={18} />
                  ) : (
                    <BsEye size={18} />
                  )}
                </button>
              </div>
              <ErrorMessage
                name="password"
                component="div"
                className="mt-1 text-sm text-red-200"
              />
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-white hover:text-purple-200"
              >
                Forgot Password?
              </Link>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-3 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {isLoading ? "Logging in..." : "Log in"}
            </motion.button>
          </Form>
        </Formik>

        <div className="mt-6 text-center">
          <p className="text-sm text-white">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              onClick={scrollToTop}
              className="font-semibold text-purple-200 hover:text-purple-100"
            >
              Register
            </Link>
          </p>
        </div>

        <div className="mt-4 text-center">
          <Link
            to="/"
            onClick={scrollToTop}
            className="flex items-center justify-center gap-2 text-sm text-white hover:text-purple-200"
          >
            <BsArrowLeft /> Back to Home Page
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Login;
